package com.flowyride.core.domain.gpx

import com.flowyride.core.domain.model.GeoPoint
import com.flowyride.core.domain.model.TrackPoint

/** A recorded or drawn line (`trk`): its segments joined end to end. */
data class GpxTrack(val name: String?, val points: List<TrackPoint>)

/** A list of points to go through (`rte`): a routing app computes the line between them. */
data class GpxRoute(val name: String?, val points: List<GpxWaypoint>)

/** What a GPX file holds, whatever app wrote it. Unnamed waypoints get an empty name. */
data class GpxFile(
    val name: String?,
    val tracks: List<GpxTrack>,
    val routes: List<GpxRoute>,
    val waypoints: List<GpxWaypoint>,
)

class GpxFormatException(message: String) : Exception(message)

/**
 * Reads GPX 1.0 / 1.1 files. Tolerant: unknown elements (extensions, time, speed…) are skipped,
 * points with invalid coordinates are dropped, tracks and routes without any point are ignored.
 */
object GpxReader {

    /** @throws GpxFormatException when [content] is not a GPX document. */
    fun read(content: String): GpxFile {
        val builder = Builder()
        var sawGpx = false
        XmlTokenizer(content).forEach { token ->
            when (token) {
                is XmlToken.Start -> {
                    if (token.name == "gpx") sawGpx = true
                    builder.start(token.name, token.attributes)
                    if (token.selfClosing) builder.end(token.name)
                }
                is XmlToken.End -> builder.end(token.name)
                is XmlToken.Text -> builder.text(token.value)
            }
        }
        if (!sawGpx) throw GpxFormatException("Not a GPX file")
        return builder.build()
    }

    private class Builder {
        private val path = ArrayList<String>()
        private val text = StringBuilder()

        private var fileName: String? = null
        private val tracks = mutableListOf<GpxTrack>()
        private val routes = mutableListOf<GpxRoute>()
        private val waypoints = mutableListOf<GpxWaypoint>()

        private var trackName: String? = null
        private var trackPoints = mutableListOf<TrackPoint>()
        private var routeName: String? = null
        private var routePoints = mutableListOf<GpxWaypoint>()

        /** The point being read (trkpt, rtept or wpt) and its children. */
        private var point: GeoPoint? = null
        private var pointName: String? = null
        private var pointElevation: Double? = null

        fun start(name: String, attributes: Map<String, String>) {
            path += name
            text.clear()
            when (name) {
                "trk" -> { trackName = null; trackPoints = mutableListOf() }
                "rte" -> { routeName = null; routePoints = mutableListOf() }
                "trkpt", "rtept", "wpt" -> {
                    point = parsePoint(attributes)
                    pointName = null
                    pointElevation = null
                }
            }
        }

        fun text(value: String) {
            text.append(value)
        }

        fun end(name: String) {
            // Tolerates unbalanced files: closes up to the matching element.
            val index = path.lastIndexOf(name)
            if (index < 0) return
            val parent = path.getOrNull(index - 1)
            val value = text.toString().trim()
            when (name) {
                "name" -> when (parent) {
                    "trk" -> trackName = value.ifEmpty { null }
                    "rte" -> routeName = value.ifEmpty { null }
                    "trkpt", "rtept", "wpt" -> pointName = value
                    "metadata" -> fileName = value.ifEmpty { null }
                    // GPX 1.0 puts the file name right under the root.
                    "gpx" -> if (fileName == null) fileName = value.ifEmpty { null }
                }
                "ele" -> if (parent in POINT_ELEMENTS) pointElevation = value.toDoubleOrNull()
                "trkpt" -> point?.let { trackPoints += TrackPoint(it, pointElevation) }
                "rtept" -> point?.let { routePoints += GpxWaypoint(pointName.orEmpty(), it) }
                "wpt" -> point?.let { waypoints += GpxWaypoint(pointName.orEmpty(), it) }
                "trk" -> if (trackPoints.size >= 2) tracks += GpxTrack(trackName, trackPoints)
                "rte" -> if (routePoints.isNotEmpty()) routes += GpxRoute(routeName, routePoints)
            }
            if (name in POINT_ELEMENTS) point = null
            while (path.size > index) path.removeAt(path.lastIndex)
            text.clear()
        }

        fun build() = GpxFile(fileName, tracks, routes, waypoints)

        private fun parsePoint(attributes: Map<String, String>): GeoPoint? {
            val lat = attributes["lat"]?.trim()?.toDoubleOrNull() ?: return null
            val lon = attributes["lon"]?.trim()?.toDoubleOrNull() ?: return null
            if (lat !in -90.0..90.0 || lon !in -180.0..180.0) return null
            return GeoPoint(lat, lon)
        }
    }

    private val POINT_ELEMENTS = setOf("trkpt", "rtept", "wpt")
}

private sealed interface XmlToken {
    /** [name] without namespace prefix. */
    data class Start(val name: String, val attributes: Map<String, String>, val selfClosing: Boolean) : XmlToken
    data class End(val name: String) : XmlToken
    data class Text(val value: String) : XmlToken
}

/** Just enough XML for GPX: elements, attributes, text, CDATA, entities. Comments, DOCTYPE and PIs are skipped. */
private class XmlTokenizer(private val xml: String) {

    fun forEach(action: (XmlToken) -> Unit) {
        var i = 0
        while (i < xml.length) {
            val lt = xml.indexOf('<', i)
            if (lt < 0) {
                emitText(xml.substring(i), action)
                return
            }
            if (lt > i) emitText(xml.substring(i, lt), action)
            i = when {
                xml.startsWith("<!--", lt) -> skipPast(lt, "-->")
                xml.startsWith("<![CDATA[", lt) -> {
                    val end = xml.indexOf("]]>", lt).let { if (it < 0) xml.length else it }
                    action(XmlToken.Text(xml.substring(lt + 9, end)))
                    (end + 3).coerceAtMost(xml.length)
                }
                xml.startsWith("<?", lt) -> skipPast(lt, "?>")
                xml.startsWith("<!", lt) -> skipPast(lt, ">")
                xml.startsWith("</", lt) -> {
                    val end = xml.indexOf('>', lt).let { if (it < 0) xml.length else it }
                    action(XmlToken.End(localName(xml.substring(lt + 2, end).trim())))
                    end + 1
                }
                else -> readStartTag(lt, action)
            }
        }
    }

    private fun skipPast(from: Int, marker: String): Int =
        xml.indexOf(marker, from).let { if (it < 0) xml.length else it + marker.length }

    private fun emitText(raw: String, action: (XmlToken) -> Unit) {
        if (raw.isNotEmpty()) action(XmlToken.Text(decodeEntities(raw)))
    }

    private fun readStartTag(lt: Int, action: (XmlToken) -> Unit): Int {
        var i = lt + 1
        val nameStart = i
        while (i < xml.length && !xml[i].isWhitespace() && xml[i] != '>' && xml[i] != '/') i++
        val name = localName(xml.substring(nameStart, i))
        val attributes = mutableMapOf<String, String>()
        while (i < xml.length) {
            while (i < xml.length && xml[i].isWhitespace()) i++
            if (i >= xml.length) break
            when (xml[i]) {
                '>' -> {
                    action(XmlToken.Start(name, attributes, selfClosing = false))
                    return i + 1
                }
                '/' -> {
                    val end = xml.indexOf('>', i).let { if (it < 0) xml.length else it }
                    action(XmlToken.Start(name, attributes, selfClosing = true))
                    return end + 1
                }
                else -> {
                    val keyStart = i
                    while (i < xml.length && xml[i] != '=' && !xml[i].isWhitespace() && xml[i] != '>' && xml[i] != '/') i++
                    val key = localName(xml.substring(keyStart, i))
                    while (i < xml.length && xml[i].isWhitespace()) i++
                    if (i < xml.length && xml[i] == '=') {
                        i++
                        while (i < xml.length && xml[i].isWhitespace()) i++
                        val quote = xml.getOrNull(i)
                        if (quote == '"' || quote == '\'') {
                            val end = xml.indexOf(quote, i + 1).let { if (it < 0) xml.length else it }
                            attributes[key] = decodeEntities(xml.substring(i + 1, end))
                            i = end + 1
                        } else {
                            val valueStart = i
                            while (i < xml.length && !xml[i].isWhitespace() && xml[i] != '>') i++
                            attributes[key] = decodeEntities(xml.substring(valueStart, i))
                        }
                    }
                }
            }
        }
        action(XmlToken.Start(name, attributes, selfClosing = false))
        return xml.length
    }

    private fun localName(qualified: String) = qualified.substringAfter(':')

    private fun decodeEntities(raw: String): String {
        if ('&' !in raw) return raw
        return ENTITY.replace(raw) { match ->
            val entity = match.groupValues[1]
            when {
                entity.startsWith("#x") || entity.startsWith("#X") ->
                    entity.substring(2).toIntOrNull(16)?.let(::codePointToString)
                entity.startsWith("#") -> entity.substring(1).toIntOrNull()?.let(::codePointToString)
                else -> NAMED_ENTITIES[entity]
            } ?: match.value
        }
    }

    private fun codePointToString(codePoint: Int): String? = when {
        codePoint < 0 || codePoint > 0x10FFFF -> null
        codePoint < 0x10000 -> codePoint.toChar().toString()
        else -> {
            val offset = codePoint - 0x10000
            charArrayOf((0xD800 + (offset shr 10)).toChar(), (0xDC00 + (offset and 0x3FF)).toChar()).concatToString()
        }
    }

    private companion object {
        val ENTITY = Regex("&(#?[A-Za-z0-9]+);")
        val NAMED_ENTITIES = mapOf("amp" to "&", "lt" to "<", "gt" to ">", "quot" to "\"", "apos" to "'")
    }
}
